import React from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Button,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import Page from "components/page";
import { useUserNameSelector } from "hooks/useNameSelector";
import { useNotifications } from "features/notifications";
import { useCheckTeamSwitch } from "hooks/useCheckTeamSwitch";
import { ValueWithLabel } from "utils/value-with-label";
import { formatDateValue } from "utils/date-format";
import {
  useGetKBApprovalDetailQuery,
  useExecuteKBRequesterActionMutation,
} from "pages/gaia-ai/kb-approval-endpoints";
import KBApprovalDocumentsTable from "./KBApprovalDocumentsTable";
import { approvalStatusMap } from "./definitions";

const KBApprovalDetailPage: React.FC = () => {
  const { reviewId } = useParams<{ reviewId: string }>();
  const { userName } = useUserNameSelector();
  const { addNotification } = useNotifications();

  const { data, isLoading, isError, refetch } = useGetKBApprovalDetailQuery(
    { reviewId: reviewId! },
    { skip: !reviewId, refetchOnMountOrArgChange: true },
  );
  useCheckTeamSwitch(data?.review?.teamName || "");

  const [executeRequesterAction, { isLoading: isCancelLoading }] =
    useExecuteKBRequesterActionMutation();

  const review = data?.review;
  const kbApprovalDocuments = data?.kbApprovalDocuments ?? [];
  const isDeletionReview = review?.reviewType === "KBDeletionReview";
  const isPending =
    review?.status?.toUpperCase() === "PENDING" ||
    review?.status?.toUpperCase() === "PENDING_APPROVAL";
  const isSubmitter = review?.requestedBy === userName;
  const isApprover = data?.pendingApprovers?.includes(userName) ?? false;

  const handleCancel = async () => {
    if (!review?.approvalId) return;
    try {
      await executeRequesterAction({
        approvalId: review.approvalId,
        parentId: review.reviewParentId,
        reviewId: reviewId!,
        action: "CANCEL_APPROVAL",
      }).unwrap();
      addNotification({
        type: "success",
        header: "Approval cancelled.",
        dismissible: true,
        timeout: 5000,
      });
      refetch();
    } catch {
      addNotification({
        type: "error",
        header: "Failed to cancel approval.",
        dismissible: true,
        timeout: 10000,
      });
    }
  };

  return (
    <Page
      type="page"
      title="KB Approval Detail"
      hideTitle
      hasBreadcrumbs
      homePage="/reviews"
      breadcrumbTitle="Reviews"
    >
      <SpaceBetween size="l">
        <Header
          variant="h1"
          actions={
            isPending && isSubmitter ? (
              <Button loading={isCancelLoading} onClick={handleCancel}>
                Cancel
              </Button>
            ) : undefined
          }
        >
          {data?.review?.reviewTitle || "KB Approval Detail"}
        </Header>
        <Container header={<Header variant="h2">Review Details</Header>}>
          {isLoading && (
            <StatusIndicator type="loading">Loading...</StatusIndicator>
          )}
          {isError && (
            <StatusIndicator type="error">
              Failed to load review details.
            </StatusIndicator>
          )}
          {review && (
            <ColumnLayout columns={4} variant="text-grid">
              <ValueWithLabel label="Status">
                {(() => {
                  const statusKey = isPending
                    ? "PENDING_APPROVAL"
                    : (data?.batchStatus ?? "");
                  const mapped = approvalStatusMap[statusKey];
                  return mapped ? (
                    <StatusIndicator type={mapped.type}>
                      {mapped.label}
                    </StatusIndicator>
                  ) : (
                    <StatusIndicator type="pending">
                      {statusKey || "-"}
                    </StatusIndicator>
                  );
                })()}
              </ValueWithLabel>
              <ValueWithLabel label="Submitted by">
                {review.requestedBy}
              </ValueWithLabel>
              <ValueWithLabel label="Approval mode">
                {review.approvalId === "auto-approved"
                  ? "Auto-approved"
                  : data.approvalMode === "AND"
                    ? "All reviewers"
                    : data.approvalMode === "OR"
                      ? "Any reviewer"
                      : "-"}
              </ValueWithLabel>
              <ValueWithLabel label="Submitted date">
                {review.createdAt ? formatDateValue(review.createdAt) : "-"}
              </ValueWithLabel>
              <ValueWithLabel label="Due date">
                {review.dueDate ? formatDateValue(review.dueDate) : "-"}
              </ValueWithLabel>
              <ValueWithLabel label="Reviewers">
                {review.reviewers?.join(", ") || "-"}
              </ValueWithLabel>
              <ValueWithLabel label="Message for reviewers">
                {review.messageForReviewers || "-"}
              </ValueWithLabel>
            </ColumnLayout>
          )}
        </Container>

        {!isPending &&
          (data?.batchStatus === "APPROVED" ||
            data?.batchStatus === "APPROVED_WITH_CONDITIONS") && (
            <Alert type="success">
              {isDeletionReview
                ? "Review completed. Documents have been submitted for deletion."
                : "Review completed. Documents have been submitted for ingestion."}
            </Alert>
          )}
        {!isPending && data?.batchStatus === "REJECTED" && (
          <Alert type="error">
            Review completed. All documents were rejected.
          </Alert>
        )}
        <KBApprovalDocumentsTable
          kbApprovalDocuments={kbApprovalDocuments}
          reviewId={reviewId!}
          teamName={review?.teamName ?? ""}
          isPending={isPending}
          isApprover={isApprover}
          isDeletionReview={isDeletionReview}
          onActionComplete={refetch}
        />
      </SpaceBetween>
    </Page>
  );
};

export default KBApprovalDetailPage;
